/**
 * Workspace URL slugs (`/{slug}/c/…`): derived from the workspace name, ASCII only, unique across workspaces and
 * never a reserved top-level path of the web app. The API derives the slug on create; clients use the same rules to
 * preview it. Pure (no I/O): the caller passes the slugs that are already taken.
 */

export const SLUG_MAX_LENGTH = 32;
/** Used when a name has no ASCII letters or digits at all (e.g. only emoji or CJK). */
export const FALLBACK_SLUG = "workspace";

/** Top-level paths owned by the app, the API or the platform — never usable as a workspace slug. */
export const RESERVED_SLUGS: readonly string[] = [
  "about", "account", "admin", "api", "app", "apps", "assets", "auth", "billing", "blog", "callback", "desktop",
  "desktop-auth", "dev", "docs", "download", "favicon", "help", "home", "invite", "invites", "join", "legal", "login",
  "logout", "m", "new", "oauth", "privacy", "pricing", "settings", "sign-in", "sign-out", "sign-up", "signin", "signout",
  "signup", "sso", "static", "status", "support", "terms", "v1", "w", "well-known", "workspaces", "www",
];

/** Letters that Unicode decomposition (NFKD) doesn't reduce to ASCII. */
const TRANSLITERATE: Record<string, string> = {
  "ß": "ss", "æ": "ae", "œ": "oe", "ø": "o", "ł": "l", "đ": "d", "ð": "d", "þ": "th", "ı": "i", "ħ": "h", "ŧ": "t",
};

function isAsciiAlnum(code: number): boolean {
  return (code >= 0x30 && code <= 0x39) || (code >= 0x61 && code <= 0x7a);
}

/** Drops trailing dashes. */
function trimTrailingDashes(s: string): string {
  let end = s.length;
  while (end > 0 && s[end - 1] === "-") end--;
  return s.slice(0, end);
}

/**
 * "São Paulo Café" → "sao-paulo-cafe": strips accents, lowercases, turns every run of other characters into one
 * dash, trims dashes and cuts at SLUG_MAX_LENGTH. May return "" (no ASCII letters or digits).
 */
export function slugify(name: string): string {
  const decomposed = name.toLowerCase().normalize("NFKD");
  let out = "";
  let pendingDash = false;
  for (const ch of decomposed) {
    const mapped = TRANSLITERATE[ch];
    const piece = mapped === undefined ? ch : mapped;
    for (let i = 0; i < piece.length; i++) {
      const code = piece.charCodeAt(i);
      if (isAsciiAlnum(code)) {
        if (pendingDash && out.length > 0) out += "-";
        pendingDash = false;
        out += piece[i];
      } else if (code >= 0x0300 && code <= 0x036f) {
        // combining accent left by NFKD: drop it without breaking the word
      } else {
        pendingDash = true;
      }
    }
    if (out.length >= SLUG_MAX_LENGTH) break;
  }
  return trimTrailingDashes(out.slice(0, SLUG_MAX_LENGTH));
}

export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.includes(slug);
}

/** A slug the user could type into the URL: 1–32 chars of a–z, 0–9 and single inner dashes. */
export function isValidSlug(slug: string): boolean {
  if (slug.length === 0 || slug.length > SLUG_MAX_LENGTH) return false;
  if (slug[0] === "-" || slug[slug.length - 1] === "-") return false;
  for (let i = 0; i < slug.length; i++) {
    const code = slug.charCodeAt(i);
    if (slug[i] === "-") {
      if (slug[i + 1] === "-") return false;
    } else if (!isAsciiAlnum(code)) return false;
  }
  return true;
}

/**
 * The slug for a new workspace named `name`, given the slugs already taken: the plain slug when it is free and not
 * reserved, otherwise the first free "<slug>-2", "<slug>-3", … (shortened so the suffix fits).
 */
export function uniqueSlug(name: string, taken: readonly string[]): string {
  const s = slugify(name);
  const base = s === "" ? FALLBACK_SLUG : s;
  const free = (candidate: string) => !isReservedSlug(candidate) && !taken.includes(candidate);
  if (free(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const candidate = `${trimTrailingDashes(base.slice(0, SLUG_MAX_LENGTH - suffix.length))}${suffix}`;
    if (free(candidate)) return candidate;
  }
}

/** Prefix shared by `slug` and every candidate uniqueSlug may produce for it (for a `LIKE 'prefix%'` lookup). */
export function slugCandidatePrefix(name: string): string {
  const s = slugify(name);
  const base = s === "" ? FALLBACK_SLUG : s;
  // The longest suffix we realistically need is "-999"; shorter prefixes only widen the lookup.
  return trimTrailingDashes(base.slice(0, SLUG_MAX_LENGTH - 4));
}
